import { MeasurementsRenderer } from "@/components/r3f/renderers/measurements/measurements-renderer";
import { PinMarkerRenderer } from "@/components/r3f/renderers/pin-marker/pin-marker-renderer";
import { WorldMapRenderer } from "@/features/world-map/world-map-renderer";
import { useSceneContextMenuEventHandlers } from "@/hooks/use-scene-context-menu-event-handlers";
import { SheetObject } from "@/object-cache";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { Measurement } from "@/store/measurement-tool-slice";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter, usesCadModel } from "@/types/scene-filter";
import {
  CachedWorldTransform,
  UniformLights,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { JSX, useCallback, useEffect } from "react";
import { Vector3 } from "three";
import { FlyWalkRenderingAnimation } from "../data-dispatch/flywalk-animation";
import { ImageType } from "../data-dispatch/flywalk-data-dispatch";
import { FlyWalkRenderables } from "../data-dispatch/flywalk-renderables";
import { AnnotationDispatch } from "../data-dispatch/use-dispatch-annotations";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";
import { FlyWalkAnnotationsRenderer } from "./flywalk-annotations-renderer";
import { FlyWalkCadRenderer } from "./flywalk-cad-renderer";
import { FlyWalkGaussianSplatsRenderer } from "./flywalk-gaussian-splats-renderer";
import { FlyWalkOrthomosaicRenderer } from "./flywalk-orthomosaic-renderer";
import { FlyWalkPanoRenderer } from "./flywalk-pano-renderer";
import { FlyWalkPathsRenderer } from "./flywalk-paths-renderer";
import { FlyWalkPhotoRenderer } from "./flywalk-photo-renderer";
import { FlyWalkPointCloudRenderer } from "./flywalk-point-cloud-renderer";
import { FlyWalkSheetsRenderer } from "./flywalk-sheets-renderer";
import { FlyWalkWaypointsRenderer } from "./flywalk-waypoints-renderer";

type FlyWalkRenderersProps = {
  renderables: FlyWalkRenderables;
  /** The current active sheets */
  sheets: Array<SheetObject | undefined>;
  /** List of placeholders to show */
  panos: IElementImg360[];
  /** List of paths to show */
  paths: IElementGenericOdometryPath[];
  /** The sheet element whose elevation should be used for 2D elements */
  sheetForElevation?: IElementGenericImgSheet;
  /** Annotations to be rendered */
  annotations: AnnotationDispatch;
  /** The current flywalk animation */
  animation: FlyWalkRenderingAnimation | null;
  /** Measurements to be rendered */
  measurements: Measurement[];
  /** The current scene filter */
  sceneFilter: SceneFilter;
  /** The current image type */
  imageType: ImageType;
  /** Whether the user is rendering an overview of the scene, or walking closely through it*/
  isOverview: boolean;
  /** Callback issued when a pano is clicked */
  onPanoClicked(pano: IElementImg360): void;
  /** Callback issued when a trajectory waypoint without a pano is clicked */
  onWalkToPosition?(position: Vector3): void;
};

/** @returns The renderers to attach to the 3JS scene in fly walk mode */
export function FlyWalkRenderers({
  renderables,
  sheets,
  panos,
  paths,
  sheetForElevation,
  annotations,
  animation,
  measurements,
  sceneFilter,
  imageType,
  isOverview,
  onPanoClicked,
  onWalkToPosition,
}: FlyWalkRenderersProps): JSX.Element | null {
  const { clippingPlanes, activeToolAndPicking } = useToolsAcquaintance();

  const onPanoTransformChanged = useRenderersHooks(renderables, sceneFilter);
  const zoomLensOn = useAppSelector(selectZoomLensActive);

  return (
    <>
      <UniformLights />
      <FlyWalkPanoRenderer
        key={renderables.pano?.id ?? KEY_ONE}
        pano={renderables.pano}
        sheetForElevation={sheetForElevation}
        pickingEnabled={!animation && sceneFilter === SceneFilter.Pano}
        customCamera={animation?.currentCamera}
        onPanoTransformChanged={onPanoTransformChanged}
      />
      <FlyWalkPanoRenderer
        key={renderables.previousPano?.id ?? KEY_TWO}
        pano={renderables.previousPano}
        sheetForElevation={sheetForElevation}
        pickingEnabled={false}
        customCamera={animation?.previousCamera}
      />
      <FlyWalkPhotoRenderer
        key={renderables.photo2D?.id ?? KEY_THREE}
        photo2D={renderables.photo2D}
      />
      <FlyWalkPhotoRenderer
        key={renderables.previousPhoto2D?.id ?? KEY_FOUR}
        photo2D={renderables.previousPhoto2D}
      />
      <FlyWalkPointCloudRenderer
        pointCloud={renderables.pointCloud}
        pickingEnabled={
          !animation &&
          (sceneFilter === SceneFilter.PointCloud ||
            sceneFilter === SceneFilter.Overlay ||
            zoomLensOn)
        }
      />
      <FlyWalkCadRenderer
        cadModel={renderables.cadModel}
        pickingEnabled={!animation && usesCadModel(sceneFilter)}
      />
      <FlyWalkGaussianSplatsRenderer
        gaussianSplats={renderables.gaussianSplats}
      />
      <FlyWalkOrthomosaicRenderer
        key={renderables.orthomosaic?.id ?? KEY_FIVE}
        orthomosaic={renderables.orthomosaic}
      />
      <FlyWalkOrthomosaicRenderer
        key={renderables.previousOrthomosaic?.id ?? KEY_SIX}
        orthomosaic={renderables.previousOrthomosaic}
      />
      <FlyWalkSheetsRenderer sheets={sheets} />
      <WorldMapRenderer
        sheet={sheetForElevation}
        pointCloud={renderables.pointCloud}
        cadModel={renderables.cadModel}
        clippingPlanes={clippingPlanes}
      />
      <FlyWalkPathsRenderer
        paths={paths}
        pano={renderables.pano?.iElement}
        sheetForElevation={sheetForElevation}
        fadeOff={!isOverview}
        onPanoClicked={onPanoClicked}
        onWalkToPosition={onWalkToPosition}
      />
      <FlyWalkWaypointsRenderer
        panos={panos}
        activePano={
          imageType === ImageType.Pano ? renderables.pano?.iElement : undefined
        }
        sheetForElevation={sheetForElevation}
        onPanoClicked={onPanoClicked}
        waypointsVisible={
          imageType !== ImageType.Photo2D &&
          !(imageType === ImageType.Pano && !annotations.isPanoAligned)
        }
        fadeOff={!isOverview}
      />
      <FlyWalkAnnotationsRenderer
        annotations={annotations}
        isAnimatingToImage={!!animation?.currentCamera}
      />
      <MeasurementsRenderer
        measurements={measurements}
        pano={
          !zoomLensOn && !!renderables.pano?.iElement
            ? renderables.pano.iElement
            : undefined
        }
        showAllMeasurementsWithPano={
          annotations.isPanoAligned &&
          (annotations.isPanoWithDepth ||
            annotations.isPanoPairedWithPickingPointCloud)
        }
        clippingPlanes={clippingPlanes}
        isPickingToolActive={activeToolAndPicking.isPickingToolActive}
      />
      {(!annotations.isPano || annotations.isPanoAligned) && (
        <PinMarkerRenderer />
      )}
    </>
  );
}

// Unique keys to label the components when the corresponding objects are null
const KEY_ONE = -1;
const KEY_TWO = -2;
const KEY_THREE = -3;
const KEY_FOUR = -4;
const KEY_FIVE = -5;
const KEY_SIX = -6;

type TransformChangedCbk = (transform: CachedWorldTransform) => void;

function useRenderersHooks(
  renderables: FlyWalkRenderables,
  sceneFilter: SceneFilter,
): TransformChangedCbk {
  const { pano, pointCloud, cadModel } = renderables;

  const { hasPointCloudBehind } = useToolsAcquaintance();

  useSceneContextMenuEventHandlers({
    panoObject:
      pano && sceneFilter === SceneFilter.Pano && !hasPointCloudBehind
        ? pano
        : undefined,
    pointCloud:
      pointCloud &&
      (sceneFilter === SceneFilter.PointCloud ||
        sceneFilter === SceneFilter.Overlay ||
        hasPointCloudBehind)
        ? pointCloud
        : undefined,
    cadModel: cadModel && usesCadModel(sceneFilter) ? cadModel : undefined,
  });

  const camera = useThree((s) => s.camera);

  const onPanoTransformChanged = useCallback(
    (pose: CachedWorldTransform) => {
      if (sceneFilter === SceneFilter.Pano) {
        camera.position.fromArray(pose.position);
      }
    },
    [camera, sceneFilter],
  );

  useEffect(() => {
    if (sceneFilter === SceneFilter.Pano) {
      // Holobuilder markups/annotations could be closer than expected
      // and we want to avoid to clip them
      camera.near = 0.01;
      camera.updateProjectionMatrix();
      return () => {
        // By default we want to keep the near plane at 10 cm, so that
        // big CAD models do not flicker
        camera.near = 0.1;
        camera.updateProjectionMatrix();
      };
    }
  }, [camera, sceneFilter]);

  return onPanoTransformChanged;
}
